import type { ReactNode } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Inbox,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatMoney } from "../lib/format";

export function Card({
  children,
  className = "",
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <section className={`card ${className}`} style={style}>
      {children}
    </section>
  );
}

export function Money({
  value,
  className = "",
}: {
  value: number;
  className?: string;
}) {
  return (
    <span className={`money ${className}`}>
      {formatMoney(value)}
    </span>
  );
}

export function StatusBadge({
  tone = "neutral",
  children,
}: {
  tone?:
    | "success"
    | "warning"
    | "danger"
    | "info"
    | "neutral";
  children: ReactNode;
}) {
  return (
    <span className={`badge badge-${tone}`}>
      {children}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  action,
  back = false,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  back?: boolean;
}) {
  const navigate = useNavigate();

  return (
    <header className="page-header">
      <div className="page-header-main">
        {back ? (
          <button
            type="button"
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Quay lại"
          >
            <ChevronLeft size={22} />
          </button>
        ) : null}

        <div>
          <h1>{title}</h1>
          {description ? (
            <p>{description}</p>
          ) : null}
        </div>
      </div>

      {action ? (
        <div className="page-header-action">
          {action}
        </div>
      ) : null}
    </header>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Inbox size={34} aria-hidden />
      <strong>{title}</strong>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export function InlineNotice({
  tone,
  title,
  children,
}: {
  tone:
    | "success"
    | "warning"
    | "danger"
    | "info";
  title: string;
  children?: ReactNode;
}) {
  const Icon =
    tone === "success"
      ? CheckCircle2
      : AlertCircle;

  return (
    <div
      className={`notice notice-${tone}`}
      role={
        tone === "danger"
          ? "alert"
          : "status"
      }
    >
      <Icon size={20} aria-hidden />
      <div>
        <strong>{title}</strong>
        {children ? <div>{children}</div> : null}
      </div>
    </div>
  );
}

export { QuantityControl } from "./QuantityControl";
export type { QuantityControlProps } from "./QuantityControl";
